import type {
  ActionConfig,
  AutomationConfig,
} from "@smartsuite/automation-engine-api";
import { observer } from "mobx-react-lite";
import type { ReactNode } from "react";
import { useState } from "react";
import ArrowRight from "../../../../generated/images/ArrowRight";
import { translateTrigger } from "../../../locales/i18n";
import { useRootStore } from "../../../stores/hooks";
import { getIntegrationLogo } from "../../../utils/integration-logo";
import {
  IntegrationsIconListWrapperStyled,
  StyledArrowRightIcon,
  StyledIconWrapper,
  ExpandedContainer,
  AnimatedIconWrapper,
  HiddenIconsWrapper,
  RestAnimatedIcon,
  RestContainer,
} from "./style";
import { Icon, Tooltip } from "@smartsuite/react-ui/lib";
import {
  getTrigger,
  getTriggerIntegration,
  getGroupActions,
  getActionTooltipText,
  CONDITIONAL_LOGIC_BADGE,
} from "./utils";

interface IntegrationsIconListProps {
  automation: AutomationConfig;
}

/** Icon for one action badge in the list. The Conditional Logic block renders
 * the same `alt-route` glyph as the automation editor (matching BranchesBlockCard)
 * instead of an integration logo; everything else uses its integration logo. */
const renderActionLogo = (
  action: ActionConfig | undefined,
  keyId: string,
): ReactNode => {
  if (action?.actionReference?.actionId === CONDITIONAL_LOGIC_BADGE) {
    // alt-route ships only in the icon pack's MISC bucket (see BranchesBlockCard).
    return (
      <Icon
        key={keyId}
        name="alt-route"
        size="MISC"
        dimension={{ width: 18, height: 18 }}
        color="currentColor"
      />
    );
  }
  return getIntegrationLogo(action?.actionReference?.integrationId ?? "")({
    key: keyId,
  });
};

export const IntegrationsIconList = observer<IntegrationsIconListProps>(
  ({ automation }) => {
    const store = useRootStore();
    const [isHover, setIsHover] = useState(false);

    const [firstAction, ...rest] = getGroupActions(automation);

    // if (firstAction === undefined) {
    //   return null;
    // }
    return (
      <IntegrationsIconListWrapperStyled>
        {/* trigger integration icon  */}
        <Tooltip
          key={`trigger-integration-icon-tooltip-${
            automation.trigger?.triggerReference?.integrationId ?? ""
          }`}
          placements={["top"]}
          text={`${
            getTriggerIntegration(store.integrations.list, automation)?.label ??
            ""
          }: ${translateTrigger(
            getTrigger(store.integrations.list, automation) ?? {},
          )}`}
          target={
            <StyledIconWrapper>
              {getIntegrationLogo(
                automation.trigger?.triggerReference?.integrationId ?? "",
              )({
                key: `trigger-integration-icon-${
                  automation.trigger?.triggerReference?.integrationId ?? ""
                }`,
              })}
            </StyledIconWrapper>
          }
        />
        <StyledArrowRightIcon>
          <ArrowRight />
        </StyledArrowRightIcon>
        {/* actions integration icons */}
        <ExpandedContainer
          onMouseEnter={() => {
            setIsHover(true);
          }}
          onMouseLeave={() => {
            setIsHover(false);
          }}
        >
          <Tooltip
            key={`action-integration-icon-tooltip-${
              firstAction?.actionReference?.actionId ?? 0
            }`}
            placements={["top"]}
            text={
              firstAction === undefined
                ? ""
                : getActionTooltipText(store.integrations.list, firstAction)
            }
            target={
              <AnimatedIconWrapper
                isHovered={rest?.length > 1 ? isHover : true}
                isAnimated
              >
                {renderActionLogo(
                  firstAction,
                  `action-integration-icon-${
                    firstAction?.actionReference?.actionId ?? 0
                  }`,
                )}
              </AnimatedIconWrapper>
            }
          />
          {rest?.length > 1 ? (
            <>
              <RestContainer isHovered={isHover}>
                {rest?.map((action, index) => {
                  return (
                    <Tooltip
                      key={`action-integration-icon-tooltip-${
                        action.actionReference?.actionId ?? index
                      }`}
                      placements={["top"]}
                      text={getActionTooltipText(
                        store.integrations.list,
                        action,
                      )}
                      target={
                        <RestAnimatedIcon isHovered={isHover} isAnimated>
                          {renderActionLogo(
                            action,
                            `action-integration-icon-${
                              action.actionReference?.actionId ?? index
                            }`,
                          )}
                        </RestAnimatedIcon>
                      }
                    />
                  );
                })}
              </RestContainer>
              <HiddenIconsWrapper style={{ zIndex: 2 }} isHovered={isHover}>
                +{rest.length}
              </HiddenIconsWrapper>
            </>
          ) : (
            rest?.map((action, index) => {
              return (
                <Tooltip
                  key={`action-integration-icon-tooltip-${
                    action.actionReference?.actionId ?? index
                  }`}
                  placements={["top"]}
                  text={
                    firstAction === undefined
                      ? ""
                      : getActionTooltipText(store.integrations.list, action)
                  }
                  target={
                    <AnimatedIconWrapper isHovered isAnimated>
                      {renderActionLogo(
                        action,
                        `action-integration-icon-${
                          action.actionReference?.actionId ?? index
                        }`,
                      )}
                    </AnimatedIconWrapper>
                  }
                />
              );
            })
          )}
        </ExpandedContainer>
      </IntegrationsIconListWrapperStyled>
    );
  },
);
